import { useMemo } from "react";
import { useProfile } from "@/hooks/useProfile";

// Season mappings by region/country
const SEASON_MAPPINGS = {
  // Default (Temperate zones - North America, Europe)
  default: [
    "Early Spring", 
    "Late Spring", 
    "Early Summer", 
    "Late Summer", 
    "Early Fall", 
    "Late Fall", 
    "Winter"
  ],

  // Ghana and West Africa
  ghana: [
    "Major Rainy Season (Apr–Jul)",
    "Minor Rainy Season (Sep–Nov)", 
    "Dry Season (Dec–Mar)"
  ],
  
  // Central America
  central_america: [
    "Primera (May–Aug)",
    "Postrera (Sep–Nov)",
    "Apante (Dec–Mar)"
  ],
  
  // Caribbean
  caribbean: [
    "Wet Season (May–Nov)",
    "Dry Season (Dec–Apr)"
  ],

  // Tropical zones (general)
  tropical: [
    "Wet Season",
    "Dry Season",
    "Transition Period"
  ],

  // Southern Hemisphere (Australia, South America, Southern Africa)
  southern_hemisphere: [
    "Summer (Dec–Feb)",
    "Autumn (Mar–May)", 
    "Winter (Jun–Aug)",
    "Spring (Sep–Nov)"
  ],

  // Mediterranean (Southern Europe, parts of California)
  mediterranean: [
    "Wet Winter (Oct–Apr)",
    "Dry Summer (May–Sep)"
  ],

  // Monsoon regions (South Asia, Southeast Asia)
  monsoon: [
    "Pre-Monsoon (Mar–May)",
    "Monsoon (Jun–Sep)",
    "Post-Monsoon (Oct–Feb)"
  ]
};

// Country/region to season mapping
const REGION_SEASON_MAP: Record<string, keyof typeof SEASON_MAPPINGS> = {
  // West Africa
  "ghana": "ghana",
  "nigeria": "ghana", 
  "burkina faso": "ghana",
  "mali": "ghana",
  "senegal": "ghana",
  "ivory coast": "ghana",
  "côte d'ivoire": "ghana",

  // Central America
  "guatemala": "central_america",
  "honduras": "central_america", 
  "el salvador": "central_america",
  "nicaragua": "central_america",
  "costa rica": "central_america",
  "panama": "central_america",
  "belize": "central_america",

  // Caribbean
  "jamaica": "caribbean",
  "haiti": "caribbean",
  "dominican republic": "caribbean",
  "puerto rico": "caribbean",
  "cuba": "caribbean",
  "barbados": "caribbean",
  "trinidad": "caribbean",
  "trinidad and tobago": "caribbean",

  // Monsoon regions
  "india": "monsoon",
  "bangladesh": "monsoon",
  "myanmar": "monsoon",
  "thailand": "monsoon",
  "vietnam": "monsoon",
  "cambodia": "monsoon",
  "laos": "monsoon",

  // Southern Hemisphere
  "australia": "southern_hemisphere",
  "new zealand": "southern_hemisphere",
  "south africa": "southern_hemisphere",
  "argentina": "southern_hemisphere",
  "chile": "southern_hemisphere",
  "brazil": "southern_hemisphere",
  "uruguay": "southern_hemisphere",

  // Mediterranean
  "spain": "mediterranean",
  "italy": "mediterranean", 
  "greece": "mediterranean",
  "portugal": "mediterranean",
  "morocco": "mediterranean",
  "tunisia": "mediterranean",
  "algeria": "mediterranean",

  // Tropical (general)
  "ecuador": "tropical",
  "colombia": "tropical",
  "venezuela": "tropical",
  "guyana": "tropical",
  "suriname": "tropical",
  "french guiana": "tropical",
  "kenya": "tropical",
  "uganda": "tropical",
  "tanzania": "tropical",
  "rwanda": "tropical",
  "burundi": "tropical",
};

// USDA Zone to season mapping (for US-based detection)
const USDA_ZONE_SEASON_MAP: Record<string, keyof typeof SEASON_MAPPINGS> = {
  // Tropical zones (Florida, Hawaii, Puerto Rico)
  "10a": "tropical", "10b": "tropical", "11a": "tropical", "11b": "tropical",
  "12a": "tropical", "12b": "tropical", "13a": "tropical", "13b": "tropical",
  
  // Mediterranean zones (Southern California)
  "9a": "mediterranean", "9b": "mediterranean",
};

/**
 * Hook to get adaptive seasons based on user's location
 */
export const useAdaptiveSeasons = () => {
  const { profile } = useProfile();

  const seasons = useMemo(() => {
    if (!profile) {
      return SEASON_MAPPINGS.default;
    }

    // First try to detect by country/location
    const location = profile.location?.toLowerCase() || "";
    
    // Check if location contains a known country/region
    for (const [country, seasonKey] of Object.entries(REGION_SEASON_MAP)) {
      if (location.includes(country)) {
        console.log(`🌍 Detected region: ${country} -> using ${seasonKey} seasons`);
        return SEASON_MAPPINGS[seasonKey];
      }
    }

    // Try USDA zone detection for US locations
    const usdaZone = profile.usda_zone?.toLowerCase() || "";
    if (usdaZone && USDA_ZONE_SEASON_MAP[usdaZone]) {
      const seasonKey = USDA_ZONE_SEASON_MAP[usdaZone];
      console.log(`🌍 Detected USDA zone: ${usdaZone} -> using ${seasonKey} seasons`);
      return SEASON_MAPPINGS[seasonKey];
    }

    // Check for tropical indicators in location
    const tropicalIndicators = ["tropical", "equatorial", "rainforest", "jungle"];
    if (tropicalIndicators.some(indicator => location.includes(indicator))) {
      console.log(`🌍 Detected tropical location -> using tropical seasons`);
      return SEASON_MAPPINGS.tropical;
    }

    // Check for southern hemisphere indicators
    const southernIndicators = ["south america", "southern", "austral"];
    if (southernIndicators.some(indicator => location.includes(indicator))) {
      console.log(`🌍 Detected southern hemisphere -> using southern_hemisphere seasons`);
      return SEASON_MAPPINGS.southern_hemisphere;
    }

    // Default to temperate seasons
    console.log(`🌍 Using default temperate seasons for location: ${location}`);
    return SEASON_MAPPINGS.default;
  }, [profile]);

  const getSeasonType = () => {
    if (!profile) return "default";
    
    const location = profile.location?.toLowerCase() || "";
    
    // Determine season type for UI purposes
    for (const [country, seasonKey] of Object.entries(REGION_SEASON_MAP)) {
      if (location.includes(country)) {
        return seasonKey;
      }
    }
    
    return "default";
  };

  return {
    seasons,
    seasonType: getSeasonType(),
    isLoading: !profile
  };
};